// ---------------------------------------------------------------------------
// Franchise + HQ Push hooks — Sprint 18 Stream C
// ---------------------------------------------------------------------------
import { useApi, useApiMutation } from "@/hooks/use-api";

// --- HQ Push Center (C8) ---

export interface PushableContent {
  id: string;
  type: "template" | "page" | "workflow" | "email_sequence" | "form";
  name: string;
  description: string;
  updatedAt: string;
}

export interface PushTarget {
  locationId: string;
  locationName: string;
  status: "pending" | "accepted" | "rejected" | "overridden";
  pushedAt: string | null;
  respondedAt: string | null;
}

export interface PushRecord {
  id: string;
  content: PushableContent;
  mode: "optional" | "recommended" | "mandatory";
  targets: PushTarget[];
  createdAt: string;
  createdBy: string;
}

export interface HqPushData {
  pushHistory: PushRecord[];
  availableContent: PushableContent[];
  locations: Array<{ id: string; name: string }>;
}

export function useHqPushData() {
  return useApi<HqPushData>("/v1/platform/franchise/push");
}

export function useCreatePush() {
  return useApiMutation<{
    contentId: string;
    mode: "optional" | "recommended" | "mandatory";
    locationIds: string[];
  }, PushRecord>(
    "/v1/platform/franchise/push",
    "POST",
  );
}

// --- Franchise Benchmarks (C10) ---

export interface LocationBenchmark {
  locationId: string;
  locationName: string;
  revenue: number;
  bookings: number;
  reviewsCount: number;
  averageRating: number;
  responseTimeHours: number;
  customerSatisfaction: number;
  rank: number;
  trend: "up" | "down" | "stable";
  aiRecommendations: string[];
}

export interface FranchiseBenchmarkData {
  locations: LocationBenchmark[];
  averages: {
    revenue: number;
    bookings: number;
    reviewsCount: number;
    averageRating: number;
    responseTimeHours: number;
  };
  topPerformer: string;
  underperformers: string[];
  period: string;
}

export function useFranchiseBenchmarks(period?: string) {
  const query = period ? `?period=${period}` : "";
  return useApi<FranchiseBenchmarkData>(`/api/franchise/benchmarks${query}`);
}
